import React, { ChangeEvent, Fragment, useCallback, useEffect, useState } from 'react';
import CardGuiaMedico from './CardGuiaMedico';
import { buscarGuiaMedico, cadastroFavorito, removeFavorito } from '../../../services/SaudeOnlineService';
import { useNavigate, useParams } from 'react-router-dom';
import { Doctor } from '../../../interfaces/entities/Doctor';
import { Button, Divider, Pagination, TextField, Typography } from '@mui/material';
import { sxDivider, sxPagination, sxTextField, sxSubtitle, sxButtonVoltar } from './sxProps';
import { deParaIDServico, deParaTextoBuscaVazia, deParaTipoServico, deParaTitulo, isFavorito, mostrarDisponibilidade } from '../TipoServicoUtils';
import FilterSelect from '../../../components/FilterSelect';
import { debounce } from '../../../utils/Debounce';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { useAccessibility } from '../../../Store/contexts/AccessibilityContext';
import LoadingApp from '../../../components/LoadingApp';
import ModalFeedBack from '../../../components/ModalFeedBack';
import { sxPaginationContrast } from '../../../assets/styles/CommonSxProps';
import { scrollTo } from '../../../utils/ScrollUtil';
import PageTitle from '../../../components/Accessibility/PageTitle';

const GuiaMedicoBusca: React.FC = () => {
  const navigate = useNavigate();

  const accessibility = useAccessibility();

  const { nomeServico } = useParams();
  const [data, setData] = useState<Doctor[]>([]);
  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);
  const [paginationCount, setPaginationCount] = useState<number>(1);
  const [searchString, setSearchString] = useState<string>('');
  const [isLoading, setLoading] = useState<boolean>(true);
  const [sortSelected, setSortSelected] = useState<string | undefined>('nome');
  const [showModalFeedBack, setShowModalFeedBack] = useState<boolean>(false);

  const paginationLength = 5;
  const siblingCount = 2;

  useEffect(() => {
    getData(1, '', 'nome');
  }, []);

  const getData = (
      pageNumber: number, 
      searchString: string | undefined, 
      sortSelected: string | undefined) => {
    setLoading(true);
    setCurrentPageNumber(pageNumber);
    buscarGuiaMedico({
      tipoServico: deParaTipoServico(nomeServico),
      start: (pageNumber - 1) * paginationLength,
      limit: getLimit(pageNumber),
      search: (searchString && searchString.length >= 3) ? searchString : undefined,
      isFavorito: isFavorito(nomeServico),
      order: sortSelected
    })
      .then(dados => {
        setData(dados.slice(0, paginationLength));
        setPaginationCount(Math.ceil(pageNumber + dados.length/paginationLength - 1))
        scrollTo('scroll-ref');
      })
      .catch(error => {
        handleShowModalFeedBack()
      })
      .finally(() => {
        setLoading(false);
      })
  }

  /* 
   * O useCallback evita que seja criada uma 
   * instância nova da função toda vez que renderiza.
   */
  const getDataDebounced = useCallback(debounce(getData, 400), []);

  /*
  * Essa lógica é pra buscar sempre o menor número necessário
  * de registros dado o comportamento do componente de paginação.
  */
  const getLimit = (pageNumber: number): number => {
    return paginationLength * Math.max(
      siblingCount + 3,
      (siblingCount * 2) + 5 - pageNumber
    )
  }

  const toggleFavorito = (doctor: Doctor) => {
    (doctor.isFavorito ? removeFavorito : cadastroFavorito)(doctor.id);
    const newData = data.map(d => {
      return d.id === doctor.id
        ? { ...d, isFavorito: !doctor.isFavorito }
        : d
    });
    setData(newData);
    if (isFavorito(nomeServico)) {
      getData(1, '', 'nome');
    }
  }

  const handlePageChange = (_: ChangeEvent<unknown>, newPage: number) => {
    getData(newPage, searchString, sortSelected);
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement | HTMLInputElement>) => {
    const value = event.target.value;
    setSearchString(value);
    if (value.length >= 3 || value.length === 0) {
      getDataDebounced(1, value, sortSelected);
    }
  };

  const handleSortSelected = (sortSelected: string | undefined) => {
    setSortSelected(sortSelected);
    getData(1, searchString, sortSelected);
  }
  
  const groupByIdPessoa = (docs: Doctor[]) => {
    const map = new Map();
    for (const doc of docs) {
      if (map.has(doc.idPessoa)) {
        map.set(doc.idPessoa, [...map.get(doc.idPessoa), doc]);
      } else {
        map.set(doc.idPessoa, [doc]);
      }
    }
    return Array.from(map.values());
  }

  const handleShowModalFeedBack = () => {
    setShowModalFeedBack(showModalFeedBack => !showModalFeedBack);
  } 

  const getIcone = (docs: Doctor[]) => {
    if (!docs || !docs.length || !nomeServico) {
      return 'default'
    } else if (isFavorito(nomeServico)) {
      return deParaIDServico(docs[0].tipoServico)
    } else {
      return nomeServico
    }
  }

  const handleBack = () => {
    //Valida se alguma busca foi realizada, caso tenha sido, apaga e realiza uma busca limpa
    if(!searchString) {
      navigate('/guiaMedico');
    } else {
      getData(1, '', 'nome');
      setSearchString('');
    }
  }

  return (
    <div className='container'>
      <LoadingApp showLoading={isLoading}/>
      <PageTitle text={deParaTitulo(nomeServico)} marginBottom="16px" />
      <div>
        <label htmlFor='pesquisarMedico' aria-label='Pesquisar por nome, endereço ou especialidade' className='select-label' style={accessibility.textStyleAccessibility}>Pesquisar por nome, endereço ou especialidade</label>
        <TextField 
          id="pesquisarMedico" 
          style={accessibility.textStyleAccessibility}
          placeholder='Exemplos: Márcio (nome), cirurgião (especialidade) ou Rua 15 (endereço)'
          variant="outlined"
          onChange={handleSearchChange}
          value={searchString} 
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: accessibility.contrastMode ? 'black' : '',
              '& fieldset': {
                borderColor: accessibility.contrastMode ? 'white' : '', 
              },
            },
          }}
          inputProps={{style: {color: accessibility.contrastMode ? 'white' : ''}}}
          fullWidth
          disabled={false}/>
      </div>
      <Divider sx={sxDivider} />

      {
        mostrarDisponibilidade(nomeServico) &&
        <div className="sort-select" id='scroll-ref'>
          <FilterSelect 
            title='Ordenar por'
            options={[
              { id: 'nome', descricao: 'Nome' }, 
              { id: 'dispon', descricao: 'Data de disponibilidade'}
            ]}
            disabled={false} 
            handleChange={handleSortSelected}
            value={sortSelected}
          />
        </div>
      }
      
      { !isLoading && !!data.length &&
        <>
          <ul style={{listStyleType: 'none', padding: '0px'}}>
          {
              groupByIdPessoa(data).map((doctors, i) =>
                <li key={i}>
                  <CardGuiaMedico
                    dados={doctors}
                    icone={getIcone(doctors)}
                    onFavoritoClicked={toggleFavorito}
                    mostrarDisponibilidade={mostrarDisponibilidade(nomeServico)}
                  />
                </li>
              )
          }
          </ul>
          {
            paginationCount > 1 &&
            <Pagination
              className='pagination-holder'
              sx={accessibility.contrastMode ? sxPaginationContrast : sxPagination}
              color='primary'
              page={currentPageNumber}
              siblingCount={siblingCount}
              boundaryCount={0}
              count={paginationCount}
              onChange={handlePageChange}
            /> 
          }
        </>
      }
      {
        !isLoading && !data.length &&
        <Typography style={{ marginTop: '25px' }}sx={sxSubtitle}>{deParaTextoBuscaVazia(nomeServico)}</Typography>
      }
      <div className="voltar-button-holder">
        <Button
          onClick={handleBack}
          startIcon={<FontAwesomeIcon style={accessibility.textStyleAccessibility} icon={faChevronLeft} color="#383838"/>}
          aria-label="Voltar para o Guia Médico"
        >
          <Typography style={accessibility.textStyleAccessibility} sx={sxButtonVoltar}>Voltar</Typography>
        </Button>
      </div>
      <ModalFeedBack
          showModal={showModalFeedBack}
          handleButtonModal={handleShowModalFeedBack}
          showButtonModal
          icon='error'
          handleShowModal={handleShowModalFeedBack}
          textButton='Voltar'
          title='Erro'
          subTitle='Erro ao buscar dados.'
      />
    </div>
  )
}

export default GuiaMedicoBusca;